import { useTrips } from 'tripPlanner/hooks/api/trip'
import useChatTripPlannerReleaseState from 'tripPlanner/hooks/useChatTripPlannerReleaseState'

interface WishlistReleaseDecision {
  isLoggedIn: boolean
  isChatTripPlannerReleaseEnabled: boolean
  hasClassicTripPlannerTrips: boolean
  isLoadingChatTripPlannerDirectEntryDecision: boolean
  hasChatTripPlannerDirectEntryDecisionError: boolean
}

export function shouldUseWishlists(
  {
    isLoggedIn,
    isChatTripPlannerReleaseEnabled,
    hasClassicTripPlannerTrips,
    isLoadingChatTripPlannerDirectEntryDecision,
    hasChatTripPlannerDirectEntryDecisionError,
  }: WishlistReleaseDecision,
): boolean {
  return (
    isLoggedIn &&
    isChatTripPlannerReleaseEnabled &&
    !hasClassicTripPlannerTrips &&
    !isLoadingChatTripPlannerDirectEntryDecision &&
    !hasChatTripPlannerDirectEntryDecisionError
  )
}

export default function useWishlistReleaseState() {
  const {
    isLoggedIn,
    isChatTripPlannerReleaseEnabled,
    hasChatTripPlannerChats,
    hasClassicTripPlannerTrips,
    isLoadingChatTripPlannerDirectEntryDecision,
    hasChatTripPlannerDirectEntryDecisionError,
  } = useChatTripPlannerReleaseState()

  // useChatTripPlannerReleaseState uses useHasClassicTripPlannerTrips which
  // calls useTrips with default options. useTrips has placeholderData: []
  // which can satisfy the query from SSR cache without a real fetch.
  //
  // For the wishlist segment decision, we need to be certain about the trip
  // state. Make a parallel trip check that forces a real fetch by overriding
  // placeholderData and staleTime. This doesn't affect useChatTripPlannerReleaseState
  // or any other consumer of useHasClassicTripPlannerTrips.
  const isEligible = isChatTripPlannerReleaseEnabled || hasChatTripPlannerChats
  const {
    data: verifiedTrips = [],
    isSuccess: tripsVerified,
    isFetching: tripsVerifying,
  } = useTrips({
    enabled: isLoggedIn && isEligible,
    placeholderData: undefined,
    staleTime: 0,
  })

  const hasVerifiedTrips = tripsVerified ? verifiedTrips.length > 0 : false
  const isVerifying = isLoggedIn && isEligible && !tripsVerified && tripsVerifying

  return {
    shouldUseWishlists: shouldUseWishlists({
      isLoggedIn,
      isChatTripPlannerReleaseEnabled: isEligible,
      hasClassicTripPlannerTrips: hasClassicTripPlannerTrips || hasVerifiedTrips,
      isLoadingChatTripPlannerDirectEntryDecision: isLoadingChatTripPlannerDirectEntryDecision || isVerifying,
      hasChatTripPlannerDirectEntryDecisionError,
    }),
    isChatTripPlannerReleaseEnabled: isEligible,
    isLoading: isLoadingChatTripPlannerDirectEntryDecision || isVerifying,
    isError: hasChatTripPlannerDirectEntryDecisionError,
  }
}
